import React, { useEffect, useState } from "react";
import { FiInfo } from "react-icons/fi";
import { Popover } from "./Popover";
import { Tooltip } from "./Tooltip";
import { cn } from "../lib/utils";

interface InfoHintProps {
  /** The text to reveal. Nothing renders when it is empty. */
  hint: string | null | undefined;
  /** Accessible name for the trigger — it has no visible text of its own. */
  label: string;
  /** What the trigger shows. Defaults to the info glyph; pass another to give the same
   *  hover-or-tap behaviour to a different marker. */
  icon?: React.ReactNode;
  /** Merged onto the trigger, for a marker that is not a plain info icon. */
  triggerClassName?: string;
  "data-testid"?: string;
}

/**
 * Whether this pointer can hover. Radix's tooltip deliberately ignores touch,
 * so a hover-only hint is unreachable on a phone; a click-only popover on a
 * desktop makes the guest click to read one sentence. This picks per device
 * rather than shipping one compromise to both.
 *
 * Tracked as state, not read once: a laptop with a touchscreen, a resized
 * responsive-design view, or a docked tablet can all change the answer while
 * the page is open.
 */
const useCanHover = (): boolean => {
  const [canHover, setCanHover] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches,
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const query = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setCanHover(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return canHover;
};

/**
 * An info icon that reveals a line of copy: on hover where the device has a
 * pointer, on tap where it doesn't.
 */
export const InfoHint: React.FC<InfoHintProps> = ({
  hint,
  label,
  icon,
  triggerClassName,
  "data-testid": testId,
}) => {
  const canHover = useCanHover();

  if (!hint) return null;

  const trigger = (
    <button
      type="button"
      aria-label={label}
      data-testid={testId ?? "info-hint-trigger"}
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-ink-secondary transition-colors hover:bg-ink/5",
        triggerClassName,
      )}
    >
      {icon ?? <FiInfo className="h-3.5 w-3.5" />}
    </button>
  );

  // The shield sits OUTSIDE, not on the button: Popover binds its toggle to a
  // wrapper around the trigger, so stopping the click at the button left the
  // icon dead on exactly the devices this branch exists for. Out here the click
  // reaches Popover's handler on the way up and is stopped before any clickable
  // chip or card above it sees it.
  const shield = (node: React.ReactNode) => (
    <span className="inline-flex" onClick={(event) => event.stopPropagation()}>
      {node}
    </span>
  );

  if (canHover) {
    return shield(
      <Tooltip label={hint} side="top">
        {trigger}
      </Tooltip>,
    );
  }

  return shield(
    <Popover
      placement="bottom"
      className="!min-w-[200px] !max-w-[280px] !rounded-2xl !p-3"
      trigger={trigger}
      content={<span className="text-xs leading-snug text-ink">{hint}</span>}
    />,
  );
};
